import { ApolloError } from '@apollo/client'
import { AuthEmailModal } from '@components/AuthEmailModal'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { HeroTooltip } from '@components/HeroTooltip'
import { DISABLE_MEMBER_AREA_REFUND } from '@constants/features'
import {
  SUBSCRIPTION_ACTIVE_STATUS,
  SUBSCRIPTION_PAST_DUE_STATUS,
  SUBSCRIPTION_ENDED_STATUS,
  SUBSCRIPTION_INACTIVE_STATUS,
} from '@constants/subscription'
import {
  PAYMENT_OVERDUE_STATUS,
  PAYMENT_REFUSED_STATUS,
} from '@constants/payment'
import { useOrderDetails } from '@contexts/OrderDetailsContext'
import { useBankSlipPrint } from '@hooks/handleBankSlip'
import { useClipboard } from '@hooks/useClipboard'
import { useRefundPayment } from '@hooks/usePayment'
import { useSession } from '@hooks/useSession'
import { useHasFeature, useThemeParams } from '@hooks/useThemeParams'
import { GestureReponderEvent } from '@tamagui/core'
import { useToastController } from '@tamagui/toast'
import { addDays } from '@utils/dateFormatter'
import parse from 'date-fns/parse'
import parseISO from 'date-fns/parseISO'
import { useLocalSearchParams } from 'expo-router'
import camelCase from 'lodash/camelCase'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Stack, Text, XStack, YStack } from 'tamagui'
import { ChangeCreditCardModal } from './ChangeCreditCardModal'
import { PaymentRefundBankSlipModal } from './PaymentRefundBankSlipModal'
import { PaymentRefundContactProducerModal } from './PaymentRefundContactProducerModal'
import { PaymentRefundErrorModal } from './PaymentRefundErrorModal'
import { PaymentRefundGenericErrorModal } from './PaymentRefundGenericErrorModal'

type ModalAction = 'changeCreditCard' | 'refundPayment' | 'cancelSubscription'

export function PaymentMethod() {
  const { action } = useLocalSearchParams<{ action?: string }>()
  const [modalAction, setModalAction] = useState(action as ModalAction)
  const isChangeCreditCardModalOpen = action === 'changeCreditCard'
  const { t } = useTranslation('OrderDetails')
  const { printBankSlip } = useBankSlipPrint()
  const {
    relevantPayment,
    subscription,
    payments = [],
    id,
    productTitle,
    createdAt,
    refetch,
    offerKind,
    selectedItem,
    allItems = [],
    isSubscription,
    isStrategySales,
  } = useOrderDetails()

  // HET-3540: venda decomposta (order bump) = estorno do aluno é POR ITEM. Sem item
  // selecionado (?item=) não há botão de estorno (não existe "estornar pedido inteiro");
  // com item, o botão estorna só ele e respeita o status do item.
  const isDecomposedOrder =
    allItems.length > 1 && allItems.some((i) => i.amount_cents != null)
  const itemAlreadyRefunded = Boolean(
    selectedItem?.item_status &&
      ['refunded', 'refund_processing'].includes(selectedItem.item_status),
  )
  const [openModal, setOpenModal] = useState(isChangeCreditCardModalOpen)
  const [openRefundBankSlipModal, setOpenRefundBankSlipModal] = useState(false)
  const [openRefundContactProducerModal, setOpenRefundContactProducerModal] =
    useState(false)
  const [openRefundErrorModal, setOpenRefundErrorModal] = useState(false)
  const [openRefundGenericErrorModal, setOpenRefundGenericErrorModal] =
    useState(false)
  const [isProcessingRefund, setIsProcessingRefund] = useState(false)
  const { logged } = useSession()
  const { copyToClipboard } = useClipboard()
  const toast = useToastController()
  const { handleRefund } = useRefundPayment()
  const { data: themeParams } = useThemeParams()

  const disableAutomaticMemberAreaRefund = useHasFeature(
    DISABLE_MEMBER_AREA_REFUND,
  )

  const hasOverdueOrRefusedPayment = payments.some((p) =>
    [PAYMENT_OVERDUE_STATUS, PAYMENT_REFUSED_STATUS].includes(p.status),
  )

  const allowChangeCreditCard =
    !!subscription &&
    relevantPayment.paymentMethod === 'credit_card' &&
    ['recurrency', 'smart_installment'].includes(
      subscription.subscription_type as string,
    ) &&
    ([SUBSCRIPTION_ACTIVE_STATUS, SUBSCRIPTION_PAST_DUE_STATUS].includes(
      subscription.status,
    ) ||
      ([SUBSCRIPTION_ENDED_STATUS, SUBSCRIPTION_INACTIVE_STATUS].includes(
        subscription.status,
      ) &&
        hasOverdueOrRefusedPayment))

  const paymentMethodTranslationKey = `paymentMethod.${camelCase(
    relevantPayment.paymentMethod,
  )}`

  const hasAlreadyRefunded =
    ['refund_analyzing', 'refunded'].includes(relevantPayment.status) ||
    itemAlreadyRefunded
  // HET-3806: o botão segue a cobrança que ainda tem código, não o
  // `relevantPayment` (a mais antiga fora de refused/error, que em pedido de
  // várias parcelas costuma ser uma já paga). Quem decide se o código ainda vale é
  // o formatPayments, então basta pegar a primeira da lista que tem um.
  //
  // A trava anterior era `payments.length === 1`, que escondia o botão em pedido
  // de várias cobranças. O que ela também fazia, sem querer, era evitar botão
  // repetido: o PaymentList renderiza logo abaixo em assinatura e carnê, com um
  // botão POR PARCELA (data, valor e status ao lado), e ele lista TODAS, não só as
  // outras. Por isso aqui o botão fica só para quem não tem lista abaixo: a compra
  // comum com mais de uma cobrança, que era o caso sem saída.
  const paymentListRenders = isSubscription || isStrategySales
  const payableCharge = paymentListRenders
    ? undefined
    : payments.find((p) => p.pixQrCode || p.bankSlipURL)
  const bankSlipURL = payableCharge?.bankSlipURL
  const pixCode = payableCharge?.pixQrCode

  const isBankSlip = relevantPayment.paymentMethod === 'bank_slip'
  const isAnalyzingRefund = relevantPayment.status === 'refund_analyzing'

  const isValidPaymentDate = (paymentDate?: string) => {
    if (!paymentDate) return false

    const [day, month, year] = paymentDate.split('/').map(Number)

    if (!day || !month || !year) return false

    const parsedPaymentDate = parse(
      `${year}-${month}-${day}`,
      'yyyy-MM-dd',
      new Date(),
    )

    if (isNaN(parsedPaymentDate.getTime())) return false

    const paymentDateRefundLimit = addDays(parsedPaymentDate.toISOString(), 7)
    return new Date() < parseISO(paymentDateRefundLimit)
  }

  // HET-3809: a janela de 7 dias tem que sair do pagamento que o resto do cálculo
  // usa. `payments[0]` é só a primeira linha da lista, que pode ser uma tentativa
  // morta com vencimento anterior, e aí o botão de estorno some antes da hora.
  const relevantPaymentDate = relevantPayment.paymentDate

  const isNotAPaymentLink = offerKind !== 'payment_link'

  const isPaymentRefundable = useMemo(() => {
    if (!relevantPayment.status || !payments?.length) return false

    // HET-3540: decomposta sem item selecionado = sem botão (estorno só por item)
    if (isDecomposedOrder && !selectedItem) return false

    const isPaymentConfirmed = ['confirmed', 'approved', 'paid'].includes(
      relevantPayment.status,
    )
    if (!isPaymentConfirmed) return false

    // Pagamento de R$ 0,00 (validação de período de teste ou compra 100% gratuita) não tem o
    // que reembolsar.
    if (!relevantPayment.totalAmountCents) return false

    return isValidPaymentDate(relevantPaymentDate)
  }, [
    relevantPayment.status,
    relevantPayment.totalAmountCents,
    payments.length,
    relevantPaymentDate,
    isDecomposedOrder,
    selectedItem,
  ])

  function handleChangeCreditCard() {
    setModalAction('changeCreditCard')
    setOpenModal(true)
  }

  function isSparkecommerceError(error: ApolloError) {
    return error.message === 'error_refund_request_failed'
  }

  async function onPressRefundButton(e?: GestureReponderEvent) {
    if (e?.stopPropagation) {
      e.stopPropagation()
    }

    if (!logged) {
      setModalAction('refundPayment')
      setOpenModal(true)
      return
    }

    if (disableAutomaticMemberAreaRefund) {
      setOpenRefundContactProducerModal(true)
      return
    }

    if (isBankSlip) {
      setOpenRefundBankSlipModal(true)
      return
    }

    try {
      setIsProcessingRefund(true)
      await handleRefund(
        relevantPayment.id.toString(),
        selectedItem?.id != null ? String(selectedItem.id) : undefined,
      )

      toast.show(t('refundSuccess.toast.title'), {
        message: t('refundSuccess.toast.message'),
        type: 'success',
      })

      await refetch()
    } catch (error: unknown) {
      if (error instanceof ApolloError && isSparkecommerceError(error)) {
        setOpenRefundErrorModal(true)
        return
      } else {
        setOpenRefundGenericErrorModal(true)
      }
    } finally {
      setIsProcessingRefund(false)
    }
  }

  if (relevantPayment.paymentMethod === null) {
    return null
  }

  return (
    <YStack gap="$size.spacing-xs">
      <H3 color="$text-primary" ff="$mulishBold" fontSize="$font-size-md">
        {t('paymentMethod.title')}
      </H3>

      <Stack
        p="$size.spacing-xs"
        br="$border-radius-sm"
        borderWidth="$borderWidth.border-width-hairline"
        borderColor="$border-strong"
        gap="$size.spacing-xxs"
        $gtSm={{
          flexDirection: 'row',
          gap: '$size.spacing-xsl',
          padding: '$size.spacing-md',
        }}
      >
        <XStack
          gap="$size.spacing-xxs"
          justifyContent="space-between"
          flexDirection="column"
          width="100%"
          $gtMd={{
            flexDirection: 'row',
          }}
        >
          <XStack
            gap={12}
            flexDirection="column"
            $gtSm={{
              flexDirection: 'row',
              gap: 48,
            }}
          >
            <XStack gap="$size.spacing-xxxs" ai="center">
              <HeroIcon
                icon={['fal', 'wallet']}
                color="$icon-primary"
                size={24}
              />
              <Text
                color="$text-primary"
                ff="$mulishRegular"
                fontSize="$font-size-xxs"
              >
                {t(paymentMethodTranslationKey)}
              </Text>
            </XStack>
            {subscription?.credit_card_masked_number && (
              <XStack gap="$size.spacing-xxs">
                <XStack gap="$size.spacing-xxxs" ai="center">
                  <HeroIcon
                    icon={['fal', 'credit-card']}
                    color="$icon-primary"
                    size={24}
                  />
                  <Text
                    color="$text-primary"
                    ff="$mulishRegular"
                    fontSize="$font-size-xxs"
                  >
                    {subscription?.credit_card_masked_number}
                  </Text>
                </XStack>
              </XStack>
            )}
          </XStack>
          <XStack
            gap="$size.spacing-xs"
            flexDirection="column"
            $gtSm={{
              flexDirection: 'row',
              gap: 48,
            }}
          >
            {isPaymentRefundable && isNotAPaymentLink && (
              <HeroTooltip
                placement="top"
                width={150}
                text={t('paymentMethod.disabledPaymentRefundTooltip')}
                display={hasAlreadyRefunded}
              >
                <Button
                  variant="primary"
                  link
                  disabled={hasAlreadyRefunded || isProcessingRefund}
                  testID="refund-button"
                  aria-label={t('paymentMethod.paymentRefundBtnLabel')}
                  onPress={onPressRefundButton}
                >
                  <Text
                    fontSize="$font-size-xxs"
                    ff="$mulishRegular"
                    color={
                      hasAlreadyRefunded || isProcessingRefund
                        ? '$text-disabled'
                        : '$accent'
                    }
                    textDecorationLine={
                      hasAlreadyRefunded || isProcessingRefund
                        ? 'none'
                        : 'underline'
                    }
                  >
                    {isProcessingRefund
                      ? t('paymentMethod.processingRefund')
                      : t('paymentMethod.paymentRefundBtnLabel')}
                  </Text>
                </Button>
              </HeroTooltip>
            )}

            {isAnalyzingRefund && (
              <HeroTooltip
                placement="top"
                width={150}
                text={t('paymentMethod.disabledPaymentRefundTooltip')}
                display={isAnalyzingRefund}
              >
                <Button variant="primary" disabled>
                  {t('paymentMethod.paymentRefundBtnLabel')}
                </Button>
              </HeroTooltip>
            )}

            {allowChangeCreditCard && (
              <Button
                variant="primary"
                $gtSm={{
                  width: 'auto',
                  marginLeft: 'auto',
                }}
                testID="change-credit-card-button"
                onPress={handleChangeCreditCard}
              >
                {t('paymentMethod.cta')}
              </Button>
            )}

            {bankSlipURL && (
              <Button
                variant="primary"
                $gtSm={{
                  width: 'auto',
                  marginLeft: 'auto',
                }}
                testID="bank-slip-button"
                onPress={() => printBankSlip(bankSlipURL)}
              >
                {t('paymentMethod.ctaBankSlip')}
              </Button>
            )}

            {pixCode && (
              <Button
                variant="primary"
                $gtSm={{
                  width: 'auto',
                  marginLeft: 'auto',
                }}
                testID="pix-code-button"
                onPress={() => copyToClipboard(pixCode)}
              >
                {t('paymentMethod.ctaPixCode')}
              </Button>
            )}
          </XStack>
        </XStack>
      </Stack>
      {logged && openModal && (
        <ChangeCreditCardModal
          open={logged && openModal}
          onClose={() => setOpenModal(false)}
        />
      )}

      <PaymentRefundBankSlipModal
        open={openRefundBankSlipModal}
        onClose={() => setOpenRefundBankSlipModal(false)}
      />

      <PaymentRefundContactProducerModal
        open={openRefundContactProducerModal}
        onClose={() => setOpenRefundContactProducerModal(false)}
        producerEmail={themeParams?.school?.email || ''}
        producerSchoolName={themeParams?.school?.name || ''}
      />

      <PaymentRefundErrorModal
        open={openRefundErrorModal}
        onClose={() => setOpenRefundErrorModal(false)}
      />

      <PaymentRefundGenericErrorModal
        open={openRefundGenericErrorModal}
        onClose={() => setOpenRefundGenericErrorModal(false)}
      />

      <AuthEmailModal
        onClose={() => setOpenModal(false)}
        open={!logged && openModal}
        orderInfo={{
          id,
          subscription,
          productTitle,
          createdAt,
        }}
        action={modalAction}
      />
    </YStack>
  )
}
